import { fromWei, hexToNumber, isHexStrict } from 'web3-utils'

import { usePreferencesStore } from '@/popup/store/PreferencesStore'
import { n, t } from '@/popup/lib/i18n'
import { Formatters } from '@/shared/i18n/formats/number'
import { Currency, CurrencySymbol } from '@/popup/lib/currencies'

const NOT_AVAILABLE = 'numbers.not_available'

/**
 * Converts and formats token value into fiat value (e.g. transfer amount or transaction fee into EUR).
 * All values must be specified in order to format a value.
 * Default values for `currentCurrency` and `currencies` are taken from `usePreferencesStore()`.
 *
 * @param value token amount convert and to format.
 * @param currentCurrency symbol of a currency preferred by the user.
 * @param currencies list of available currencies.
 * @param applyRounding true if the rounding should be applied or false the most precise result should be returned.
 * @returns formatted value e.g. '€42,01'.
 */
const formatCurrency = (
  value?: string,
  currentCurrency?: CurrencySymbol,
  currencies?: Currency,
  applyRounding: boolean = true
): string => {
  const preferencesStore = usePreferencesStore()
  const _currencies = currencies ?? preferencesStore.currencies
  const _currentCurrency = currentCurrency ?? preferencesStore.currentCurrency

  if (!_currentCurrency) {
    return t(NOT_AVAILABLE)
  }
  if (!value) {
    return `${_currentCurrency} ${t(NOT_AVAILABLE)}`
  }

  const currencyMultiplier: number | undefined = _currencies[_currentCurrency]

  if (!currencyMultiplier) {
    return `${_currentCurrency} ${t(NOT_AVAILABLE)}`
  }

  const numberValue = Number(fromWei(value)) * currencyMultiplier
  if (!applyRounding) {
    // Depending on locale we may get slightly different currency symbol for the same currency
    const numberFormat = new Intl.NumberFormat(preferencesStore.locale, {
      style: 'currency',
      currency: _currentCurrency,
    })

    const currencySymbol = numberFormat
      .formatToParts(numberValue)
      .map((item, index) => ({ ...item, index }))
      .find(item => item.type === 'currency')
    if (currencySymbol) {
      return currencySymbol.index === 0
        ? `${currencySymbol.value}${numberValue}`
        : `${numberValue}${currencySymbol.value}`
    }
  }

  let formattedCurrency: string
  if (numberValue > 1_000_000_000) {
    formattedCurrency = '> ' + n(1_000_000_000, 'currency')
  } else if (numberValue < 0.01 && numberValue > 0) {
    formattedCurrency = '< ' + n(0.01, 'currency')
  } else {
    formattedCurrency = n(numberValue, 'currency')
  }

  return formattedCurrency
}

/**
 * Attempts to format a value according to the selected formatting style.
 * @param value hex or decimal.
 * @param key the selected formatting style. Default is 'decimal'.
 * @returns formatted number or empty string in case of an error, e.g. if `value` is undefined.
 */
const formatNumber = (value?: number | string, key?: Formatters): string => {
  let value_ = value

  if (value_ === undefined) {
    return ''
  }

  if (isHexStrict(value_)) {
    value_ = hexToNumber(value_)
  }

  if (typeof value_ === 'string') {
    value_ = Number(value_)
  }

  return n(value_, key ?? 'decimal')
}

export default (): {
  formatCurrency: (
    value?: string,
    currentCurrency?: CurrencySymbol,
    currencies?: Currency,
    applyRounding?: boolean
  ) => string
  formatNumber: (value?: number | string, key?: Formatters) => string
} => {
  return {
    formatCurrency,
    formatNumber,
  }
}
